import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { FileXls, FilePdf, FunnelSimple } from "@phosphor-icons/react";
import { iso } from "@/components/finance/apShared";

export default function BillsExportPanel() {
  const today = new Date();
  const [reportStart, setReportStart] = useState(() => iso(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [reportEnd, setReportEnd] = useState(() => iso(new Date(today.getFullYear(), today.getMonth() + 1, 0)));
  const [reportStatus, setReportStatus] = useState(""); // '' | 'pending' | 'paid'
  const [exporting, setExporting] = useState(false);

  const setQuickRange = (kind) => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    let s;
    let e;
    switch (kind) {
      case "this-month":
        s = new Date(y, m, 1); e = new Date(y, m + 1, 0); break;
      case "next-month":
        s = new Date(y, m + 1, 1); e = new Date(y, m + 2, 0); break;
      case "next-30":
        s = now; e = new Date(y, m, now.getDate() + 30); break;
      case "next-90":
        s = now; e = new Date(y, m, now.getDate() + 90); break;
      case "year":
        s = new Date(y, 0, 1); e = new Date(y, 11, 31); break;
      default:
        return;
    }
    setReportStart(iso(s));
    setReportEnd(iso(e));
  };

  const exportBills = async (format) => {
    if (!reportStart || !reportEnd) { toast.error("Informe início e fim do período"); return; }
    setExporting(true);
    try {
      const params = new URLSearchParams({ format, start: reportStart, end: reportEnd });
      if (reportStatus) params.append("status", reportStatus);
      const { data, headers } = await api.get(`/bills/export?${params.toString()}`, { responseType: "blob" });
      const blob = new Blob([data], { type: headers["content-type"] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const ext = format === "pdf" ? "pdf" : "xlsx";
      a.download = `contas_a_pagar_${reportStart}_a_${reportEnd}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exportado como ${ext.toUpperCase()}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao exportar");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="mt-8 border border-black bg-white p-4 md:p-5" data-testid="bills-export-panel">
      <div className="flex items-center gap-2 mb-4">
        <FunnelSimple size={18} weight="bold" />
        <div>
          <div className="overline text-zinc-500" style={{ fontSize: 10 }}>RELATÓRIO / EXPORTAÇÃO</div>
          <div className="font-head text-lg font-bold">Escolha um período e baixe em Excel ou PDF</div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        <div>
          <label className="label">Início</label>
          <input type="date" className="input font-mono" value={reportStart} onChange={(e) => setReportStart(e.target.value)} data-testid="export-start" />
        </div>
        <div>
          <label className="label">Fim</label>
          <input type="date" className="input font-mono" value={reportEnd} onChange={(e) => setReportEnd(e.target.value)} data-testid="export-end" />
        </div>
        <div>
          <label className="label">Status</label>
          <select className="input" value={reportStatus} onChange={(e) => setReportStatus(e.target.value)} data-testid="export-status">
            <option value="">Todos</option>
            <option value="pending">Pendentes</option>
            <option value="paid">Pagos</option>
          </select>
        </div>
        <div className="md:col-span-2 flex items-end gap-2">
          <button type="button" disabled={exporting} className="btn flex-1" onClick={() => exportBills("xlsx")} data-testid="export-xlsx">
            <FileXls size={16} weight="bold" /> {exporting ? "..." : "Excel"}
          </button>
          <button type="button" disabled={exporting} className="btn flex-1" onClick={() => exportBills("pdf")} data-testid="export-pdf">
            <FilePdf size={16} weight="bold" /> {exporting ? "..." : "PDF"}
          </button>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2 flex-wrap text-xs">
        <span className="text-zinc-500">Atalhos:</span>
        <button type="button" onClick={() => setQuickRange("this-month")} className="underline hover:text-black" data-testid="quick-this-month">Este mês</button>
        <span className="text-zinc-300">·</span>
        <button type="button" onClick={() => setQuickRange("next-month")} className="underline hover:text-black" data-testid="quick-next-month">Próximo mês</button>
        <span className="text-zinc-300">·</span>
        <button type="button" onClick={() => setQuickRange("next-30")} className="underline hover:text-black" data-testid="quick-next-30">Próximos 30 dias</button>
        <span className="text-zinc-300">·</span>
        <button type="button" onClick={() => setQuickRange("next-90")} className="underline hover:text-black" data-testid="quick-next-90">Próximos 90 dias</button>
        <span className="text-zinc-300">·</span>
        <button type="button" onClick={() => setQuickRange("year")} className="underline hover:text-black" data-testid="quick-year">Ano inteiro</button>
      </div>
    </div>
  );
}
